import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, PieChart as PieChartIcon, TrendingUp, Users, Calendar, BarChart3, LineChart } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, LineChart as ReLineChart, Line, Area, AreaChart } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const CATEGORY_COLORS = {
  food: '#f97316',
  transport: '#3b82f6',
  shopping: '#ec4899',
  entertainment: '#8b5cf6',
  utilities: '#6b7280',
  rent: '#22c55e',
  general: '#64748b'
};

const CHART_COLORS = ['#8b5cf6', '#06b6d4', '#f97316', '#22c55e', '#ec4899', '#eab308', '#ef4444'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-xl p-3 shadow-lg">
        <p className="text-sm font-medium">{label || payload[0]?.name}</p>
        <p className="text-lg font-bold text-violet-600">₹{payload[0]?.value?.toLocaleString()}</p>
      </div>
    );
  }
  return null;
};

const Analytics = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [members, setMembers] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeChart, setActiveChart] = useState('pie'); // pie, bar, line

  useEffect(() => {
    fetchData();
  }, [groupId]);

  const fetchData = async () => {
    try {
      const [groupRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}`),
        axios.get(`${API}/analytics/group/${groupId}`)
      ]);
      setGroup(groupRes.data);
      setAnalytics(analyticsRes.data);
      
      // Fetch member names
      const memberIds = Object.keys(analyticsRes.data.member_spending || {});
      const memberPromises = memberIds.map(id => 
        axios.get(`${API}/users/${id}`).catch(() => ({ data: { id, name: 'Unknown' } }))
      );
      const membersData = await Promise.all(memberPromises);
      const membersMap = {};
      membersData.forEach(res => { membersMap[res.data.id] = res.data.name; });
      setMembers(membersMap);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const categoryTotal = Object.values(analytics?.category_breakdown || {}).reduce((a, b) => a + b, 0);
  const memberTotal = Object.values(analytics?.member_spending || {}).reduce((a, b) => a + b, 0);

  // Prepare chart data
  const pieData = Object.entries(analytics?.category_breakdown || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
    color: CATEGORY_COLORS[name] || '#6b7280'
  }));

  const memberBarData = Object.entries(analytics?.member_spending || {})
    .sort((a, b) => b[1] - a[1])
    .map(([id, value], index) => ({
      name: members[id] || 'Unknown',
      amount: value,
      fill: CHART_COLORS[index % CHART_COLORS.length]
    }));

  const timelineData = Object.entries(analytics?.expense_timeline || {})
    .sort((a, b) => new Date(a[0]) - new Date(b[0]))
    .slice(-14) // Last 14 days
    .map(([date, amount]) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      expenses: amount,
      contributions: analytics?.contribution_timeline?.[date] || 0
    }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate(`/group/${groupId}`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold mb-2" data-testid="analytics-title">Analytics</h1>
          <p className="text-muted-foreground mb-8">Financial insights for {group?.name}</p>
        </motion.div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-2xl p-6 border border-border"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <p className="text-sm text-muted-foreground">Goal Progress</p>
            </div>
            <p className="text-3xl font-bold">{(analytics?.goal_progress || 0).toFixed(1)}%</p>
            <div className="w-full h-2 bg-muted rounded-full mt-3 overflow-hidden">
              <div
                className="h-full gradient-primary rounded-full"
                style={{ width: `${Math.min(analytics?.goal_progress || 0, 100)}%` }}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-2xl p-6 border border-border"
          >
            <div className="flex items-center gap-2 mb-2">
              <PieChart className="w-5 h-5 text-violet-600" />
              <p className="text-sm text-muted-foreground">Total Expenses</p>
            </div>
            <p className="text-3xl font-bold">₹{(analytics?.total_expenses || 0).toLocaleString()}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-2xl p-6 border border-border"
          >
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-muted-foreground">Total Contributions</p>
            </div>
            <p className="text-3xl font-bold">₹{(analytics?.total_contributions || 0).toLocaleString()}</p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Category Breakdown - Interactive Pie Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card rounded-2xl p-6 border border-border"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Spending by Category</h2>
              <div className="flex gap-1 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setActiveChart('pie')}
                  className={`p-2 rounded-md transition-colors ${activeChart === 'pie' ? 'bg-violet-600 text-white' : 'hover:bg-muted-foreground/10'}`}
                  data-testid="chart-pie-btn"
                >
                  <PieChartIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setActiveChart('bar')}
                  className={`p-2 rounded-md transition-colors ${activeChart === 'bar' ? 'bg-violet-600 text-white' : 'hover:bg-muted-foreground/10'}`}
                  data-testid="chart-bar-btn"
                >
                  <BarChart3 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {pieData.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No expenses yet</p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  {activeChart === 'pie' ? (
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  ) : (
                    <BarChart data={pieData} layout="vertical">
                      <XAxis type="number" tickFormatter={(v) => `₹${v}`} />
                      <YAxis type="category" dataKey="name" width={80} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            )}
            
            {/* Category Legend */}
            {pieData.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-4 justify-center">
                {pieData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-xs text-muted-foreground">{item.name}</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Member Spending - Bar Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card rounded-2xl p-6 border border-border"
          >
            <h2 className="text-lg font-semibold mb-4">Spending by Member</h2>
            {memberBarData.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No expenses yet</p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={memberBarData}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => `₹${v}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                      {memberBarData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </motion.div>
        </div>

        {/* Timeline Chart - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-card rounded-2xl p-6 border border-border mt-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <LineChart className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-semibold">Activity Over Time</h2>
          </div>
          {timelineData.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No activity data yet</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timelineData}>
                  <defs>
                    <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="contribGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `₹${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="expenses" 
                    stroke="#8b5cf6" 
                    fill="url(#expenseGradient)"
                    strokeWidth={2}
                    name="Expenses"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="contributions" 
                    stroke="#22c55e" 
                    fill="url(#contribGradient)"
                    strokeWidth={2}
                    name="Contributions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </motion.div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-card rounded-2xl p-6 border border-border mt-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-semibold">Recent Activity</h2>
          </div>
          {Object.keys(analytics?.expense_timeline || {}).length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No activity to show</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(analytics?.expense_timeline || {})
                .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                .slice(0, 10)
                .map(([date, amount]) => (
                  <div key={date} className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
                    <span className="text-sm text-muted-foreground">
                      {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    <span className="font-semibold">₹{amount.toLocaleString()}</span>
                  </div>
                ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Analytics;
